import { messaging } from './config';
import { getToken, onMessage } from 'firebase/messaging';

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export const getFCMToken = async () => {
  try {
    // Request notification permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      // Get FCM token without VAPID key (for receiving only)
      const token = await getToken(messaging);
      
      if (token) {
        console.log('FCM Token obtained:', token);
        return token;
      } else {
        console.log('No registration token available.');
        return null;
      }
    } else {
      console.log('Notification permission denied:', permission);
      return null;
    }
  } catch (error) {
    console.error('Error occurred while retrieving token:', error);
    return null;
  }
};

/**
 * Set up message listener for foreground messages
 * @param {Function} onMessageReceived - Callback function to handle received messages
 * @returns {Function} Unsubscribe function
 */
export const setupForegroundMessageListener = (onMessageReceived) => {
  return onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    
    // Call the provided callback with the message payload
    if (onMessageReceived && typeof onMessageReceived === 'function') {
      onMessageReceived(payload);
    }
  });
};

/**
 * Check if notifications are supported by the browser
 * @returns {boolean} True if notifications are supported
 */
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Check current notification permission status
 * @returns {string} Permission status: 'default', 'granted', or 'denied'
 */
export const getNotificationPermission = () => {
  return Notification.permission;
};

/**
 * Initialize Firebase messaging with error handling
 * @returns {Promise<boolean>} True if initialization successful
 */
export const initializeMessaging = async () => {
  try {
    if (!isNotificationSupported()) {
      console.warn('This browser does not support notifications');
      return false;
    }

    const token = await getFCMToken();
    return token !== null;
  } catch (error) {
    console.error('Failed to initialize messaging:', error);
    return false;
  }
};
